import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "../css/nationalDashboard.css";
import Navbar from "../components/navbar";
import { TalksDashboardPanel } from "./talkManager";
import Orders from "./orders";
import ChaptersPage from "./chapters";
import DonationReport from "./donationReport";
import { NationalTeamWidget, UsersManager } from "./users";
import {
    collection,
    getDocs,
    getDoc,
    query,
    where,
    Timestamp,
    addDoc,
    doc,
    updateDoc,
    serverTimestamp,
    orderBy,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
const TOPICS_SHEET_URL =
    "https://docs.google.com/spreadsheets/d/1-MiYE6bK06JNjKirI-AaqZbE3IvXok2ifhVZxbbayYk/edit?gid=0#gid=0";
const MEDIA_SHEET_URL =
    "https://docs.google.com/spreadsheets/d/1KK60naAM1po-Qyv5mGgeDzBdCThnPVlIXou6nadt_Kc/edit?usp=sharing";

const COL = {
    orders: "orders",
    talks: "talks",
    chapters: "chapters",
    subscribers: "subscribers",
    hqNotes: "hq_notes",
    hqAlerts: "hq_alerts",
};

/**
 * National (SOLE HQ) dashboard with sidebar navigation.
 *
 * Home = overview (current grid)
 * Talks = full-width talks dashboard
 * Orders = placeholder (we’ll refactor Orders into a drop-in screen next)
 */

function StatCard({ label, value, sublabel }) {
    return (
        <div className="nd-card nd-stat-card">
            <div className="nd-stat-label">{label}</div>
            <div className="nd-stat-value">{value}</div>
            {sublabel ? <div className="nd-stat-sublabel">{sublabel}</div> : null}
        </div>
    );
}

function Panel({ title, actions, children, className = "" }) {
    return (
        <section className={`nd-card nd-panel ${className}`.trim()}>
            <div className="nd-panel-header">
                <div className="nd-panel-title">{title}</div>
                <div className="nd-panel-actions">{actions}</div>
            </div>
            <div className="nd-panel-body">{children}</div>
        </section>
    );
}

function SimpleModal({ open, title, children, onClose }) {
    if (!open) return null;
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={onClose}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(2,6,23,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px",
                zIndex: 9999,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(620px, 100%)",
                    background: "rgba(255,255,255,0.95)",
                    border: "1px solid rgba(15,23,42,0.12)",
                    borderRadius: "18px",
                    boxShadow: "0 24px 60px rgba(2,6,23,0.35)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "14px 16px",
                        borderBottom: "1px solid rgba(15,23,42,0.10)",
                    }}
                >
                    <div style={{ fontWeight: 900, fontSize: "1.0rem" }}>{title}</div>
                    <button
                        type="button"
                        className="nd-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        Close
                    </button>
                </div>
                <div style={{ padding: "16px" }}>{children}</div>
            </div>
        </div>
    );
}

function Segmented({ value, onChange, options }) {
    return (
        <div className="nd-segmented" role="tablist" aria-label="Range selector">
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    className={`nd-seg-btn ${value === opt.value ? "is-active" : ""}`.trim()}
                    onClick={() => onChange(opt.value)}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

function SideNavItem({ active, onClick, icon, label }) {
    return (
        <button
            type="button"
            className={`nd-sidenav-item ${active ? "is-active" : ""}`.trim()}
            onClick={onClick}
        >
            {icon ? (
                <span className="nd-sidenav-icon" aria-hidden="true">
                    {icon}
                </span>
            ) : null}
            <span className="nd-sidenav-label">{label}</span>
        </button>
    );
}

const NAV_TABS = ["home", "talks", "chapters", "orders", "donations", "users"];
const ACTIVE_TAB_STORAGE_KEY = "nd:activeTab";

export default function NationDashboard({ currentUser }) {
    // Sidebar tab — persisted to the URL (?tab=) and localStorage so a refresh or
    // a browser "back" from the talk editor returns to the same tab, not Home.
    const [searchParams, setSearchParams] = useSearchParams();
    const [active, setActiveState] = useState(() => {
        const fromUrl = searchParams.get("tab");
        if (fromUrl && NAV_TABS.includes(fromUrl)) return fromUrl;
        try {
            const fromStore = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
            if (fromStore && NAV_TABS.includes(fromStore)) return fromStore;
        } catch {
            // localStorage unavailable — fall through
        }
        return "home";
    });

    const setActive = useCallback((next) => {
        if (!NAV_TABS.includes(next)) return;
        setActiveState(next);
        try {
            localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, next);
        } catch {
            // ignore
        }
        setSearchParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                p.set("tab", next);
                return p;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    // Keep the URL in sync on first load so the tab is shareable / refreshable
    // even before the user clicks another tab.
    useEffect(() => {
        if (searchParams.get("tab") !== active) {
            setSearchParams(
                (prev) => {
                    const p = new URLSearchParams(prev);
                    p.set("tab", active);
                    return p;
                },
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Layout-only state (wire to real data later)
    const [range, setRange] = useState("mtd");

    // Bump this to tell the embedded <Orders> screen to open its inventory modal.
    const [ordersInventoryToken, setOrdersInventoryToken] = useState(0);

    const [metrics, setMetrics] = useState({
        revenueCents: null,
        unfulfilledOrders: null,
        activeChapters: null,
        upcomingTalks14d: null,
        emailSubscribers: null,
        salesSummary: null,
        inventoryData: null,
        inventorySummary: null,
        chaptersList: null,

    });
    const [authUser, setAuthUser] = useState(() => {
        try {
            return getAuth().currentUser || null;
        } catch {
            return null;
        }
    });

    useEffect(() => {
        let unsub = null;
        try {
            const auth = getAuth();
            unsub = onAuthStateChanged(auth, (u) => setAuthUser(u || null));
        } catch {
            // ignore
        }
        return () => {
            if (typeof unsub === "function") unsub();
        };
    }, []);

    const effectiveUser = currentUser || authUser;

    const [metricsError, setMetricsError] = useState("");

    // HQ Notes / Alerts
    const [hqNotes, setHqNotes] = useState([]);
    const [hqAlerts, setHqAlerts] = useState([]);
    const [notesError, setNotesError] = useState("");

    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [alertModalOpen, setAlertModalOpen] = useState(false);

    const [newNoteText, setNewNoteText] = useState("");
    const [newAlertText, setNewAlertText] = useState("");
    const [currentUserName, setCurrentUserName] = useState("");

    useEffect(() => {
        let mounted = true;

        const loadUserProfileName = async () => {
            try {
                const uid = effectiveUser?.uid || effectiveUser?.id;
                if (!uid) {
                    if (mounted) setCurrentUserName("");
                    return;
                }

                // Prefer the canonical profile doc in Firestore (same approach as the talk editor's user labeling)
                const snap = await getDoc(doc(db, "users", uid));
                if (snap.exists()) {
                    const data = snap.data() || {};
                    const label = (data.displayName || data.name || data.email || "").toString().trim();
                    if (mounted) setCurrentUserName(label);
                    return;
                }

                if (mounted) setCurrentUserName("");
            } catch {
                if (mounted) setCurrentUserName("");
            }
        };

        loadUserProfileName();
        return () => {
            mounted = false;
        };
    }, [effectiveUser]);

    const currentUserLabel = useMemo(() => {
        // 1) Firestore profile name, if available
        if (currentUserName && currentUserName.trim()) return currentUserName.trim();

        // 2) Fallbacks from auth/currentUser prop
        const u = effectiveUser || {};
        return (u.displayName || u.name || u.email || "").toString().trim();
    }, [currentUser, currentUserName]);

    const loadHqNotesAndAlerts = async () => {
        setNotesError("");
        try {
            // Notes
            let notes = [];
            try {
                const snap = await getDocs(query(collection(db, COL.hqNotes), orderBy("createdAt", "desc")));
                snap.forEach((d) => {
                    const data = d.data() || {};
                    if (data.dismissed) return;
                    notes.push({
                        id: d.id,
                        text: data.text || "",
                        authorName: data.authorName || data.author || "",
                        createdAt: data.createdAt || null,
                    });
                });
            } catch {
                const snap = await getDocs(collection(db, COL.hqNotes));
                snap.forEach((d) => {
                    const data = d.data() || {};
                    if (data.dismissed) return;
                    notes.push({
                        id: d.id,
                        text: data.text || "",
                        authorName: data.authorName || data.author || "",
                        createdAt: data.createdAt || null,
                    });
                });
            }

            // Alerts
            let alerts = [];
            try {
                const snap = await getDocs(query(collection(db, COL.hqAlerts), orderBy("createdAt", "desc")));
                snap.forEach((d) => {
                    const data = d.data() || {};
                    if (data.dismissed) return;
                    alerts.push({
                        id: d.id,
                        text: data.text || "",
                        authorName: data.authorName || data.author || "",
                        createdAt: data.createdAt || null,
                    });
                });
            } catch {
                const snap = await getDocs(collection(db, COL.hqAlerts));
                snap.forEach((d) => {
                    const data = d.data() || {};
                    if (data.dismissed) return;
                    alerts.push({
                        id: d.id,
                        text: data.text || "",
                        authorName: data.authorName || data.author || "",
                        createdAt: data.createdAt || null,
                    });
                });
            }

            setHqNotes(notes);
            setHqAlerts(alerts);
        } catch {
            setNotesError("Could not load notes/alerts.");
            setHqNotes([]);
            setHqAlerts([]);
        }
    };

    const dismissHqItem = async (kind, id) => {
        try {
            const colName = kind === "note" ? COL.hqNotes : COL.hqAlerts;
            await updateDoc(doc(db, colName, id), { dismissed: true, dismissedAt: serverTimestamp() });
            if (kind === "note") setHqNotes((prev) => prev.filter((n) => n.id !== id));
            else setHqAlerts((prev) => prev.filter((a) => a.id !== id));
        } catch {
            // no-op (keeps UI stable)
        }
    };

    const addHqItem = async (kind) => {
        const isNote = kind === "note";
        const authorName = (currentUserLabel || currentUser?.email || "Unknown").toString().trim();
        const text = (isNote ? newNoteText : newAlertText).trim();
        if (!text) return;

        const colName = isNote ? COL.hqNotes : COL.hqAlerts;
        try {
            await addDoc(collection(db, colName), {
                authorName,
                authorId: (currentUser && (currentUser.uid || currentUser.id)) || null,
                authorEmail: (currentUser && currentUser.email) || null,
                text,
                dismissed: false,
                createdAt: serverTimestamp(),
            });

            if (isNote) {
                setNewNoteText("");
                setNoteModalOpen(false);
            } else {
                setNewAlertText("");
                setAlertModalOpen(false);
            }

            await loadHqNotesAndAlerts();
        } catch {
            setNotesError("Could not save to Firebase.");
        }
    };

    const rangeStart = useMemo(() => {
        const now = new Date();
        const start = new Date(now);

        if (range === "today") {
            start.setHours(0, 0, 0, 0);
            return start;
        }

        if (range === "week") {
            const day = start.getDay();
            const diff = (day === 0 ? -6 : 1) - day; // Monday start
            start.setDate(start.getDate() + diff);
            start.setHours(0, 0, 0, 0);
            return start;
        }

        if (range === "ytd") {
            start.setMonth(0, 1);
            start.setHours(0, 0, 0, 0);
            return start;
        }

        start.setDate(1); // MTD
        start.setHours(0, 0, 0, 0);
        return start;
    }, [range]);

    const formatMoney = (cents) => {
        if (cents == null) return "—";
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
    };

    // --- Sales summary helpers (match Orders page logic) ---
    const normalizeToDate = (raw) => {
        if (!raw) return null;
        if (raw instanceof Date) return raw;
        if (raw && typeof raw.toDate === "function") return raw.toDate();
        const d = new Date(raw);
        return isNaN(d) ? null : d;
    };

    // Match Orders.getOrderDate(): compare at local-midnight so date-only strings behave.
    const getOrderDateMidnight = (raw) => {
        if (!raw) return null;
        if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            const [yyyy, mm, dd] = raw.split("-").map(Number);
            return new Date(yyyy, mm - 1, dd);
        }
        const d = normalizeToDate(raw);
        if (!d) return null;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    // When an order was *placed*: earliest "Ordered" node in the status timeline,
    // else earliest node of any kind, else the stored date. Never the ship/fulfill
    // date, so sales stay bucketed by when the customer ordered.
    const getOrderPlacedMidnight = (o) => {
        const history = Array.isArray(o?.statusHistory) ? o.statusHistory : [];
        const dated = history
            .map((h) => ({ status: h?.status, at: normalizeToDate(h?.at) }))
            .filter((h) => h.at);
        if (dated.length) {
            const ordered = dated
                .filter((h) => h.status === "Ordered")
                .sort((a, b) => a.at - b.at)[0];
            const chosen = ordered || dated.sort((a, b) => a.at - b.at)[0];
            return new Date(chosen.at.getFullYear(), chosen.at.getMonth(), chosen.at.getDate());
        }
        return getOrderDateMidnight(o?.date);
    };

    const normalizeTotalDollars = (v) => {
        if (typeof v === "number") return v;
        if (typeof v === "string") {
            const n = parseFloat(v.replace(/[^0-9.\-]/g, ""));
            return Number.isFinite(n) ? n : 0;
        }
        return 0;
    };

    const normalizeQuantity = (v) => {
        if (typeof v === "number") return v;
        const n = Number(v || 0);
        return Number.isFinite(n) ? n : 0;
    };

    const computeSalesSummary = (ordersList, startDate) => {
        const now = new Date();
        const startMid = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endMid.setDate(endMid.getDate() + 1);

        const summary = {
            totalOrders: 0,
            totalSalesCents: 0,
            avgOrderValueCents: 0,
            totalCandles: 0,
            totalShirts: 0,
            candleSalesCents: 0,
            shirtSalesCents: 0,
            byPayment: {},
        };

        (ordersList || []).forEach((o) => {
            const d = getOrderPlacedMidnight(o);
            if (!d) return;
            if (d < startMid || d >= endMid) return;

            const totalDollars = normalizeTotalDollars(o?.total);
            const qty = normalizeQuantity(o?.quantity);
            const cents = Math.round(totalDollars * 100);

            summary.totalOrders += 1;
            summary.totalSalesCents += cents;

            const item = (o?.item || "").trim();
            const isCandle = item === "Candle";
            const isShirt = item === "T-Shirt" || item === "T-shirt" || item === "T Shirt" || item === "Shirt";

            if (isCandle) {
                summary.totalCandles += qty;
                summary.candleSalesCents += cents;
            } else if (isShirt) {
                summary.totalShirts += qty;
                summary.shirtSalesCents += cents;
            }

            const method = (o?.paymentMethod || "Other").trim() || "Other";
            if (!summary.byPayment[method]) summary.byPayment[method] = { orders: 0, salesCents: 0 };
            summary.byPayment[method].orders += 1;
            summary.byPayment[method].salesCents += cents;
        });

        if (summary.totalOrders > 0) {
            summary.avgOrderValueCents = Math.round(summary.totalSalesCents / summary.totalOrders);
        }

        return summary;
    };

    // --- Inventory helpers (match Orders inventory modal logic) ---
    const normalizeBatches = (batches) => {
        const arr = Array.isArray(batches) ? batches : [];
        // Oldest first (FIFO consumption)
        return [...arr].sort((a, b) => {
            const da = normalizeToDate(a?.createdAt) || new Date(0);
            const db = normalizeToDate(b?.createdAt) || new Date(0);
            return da - db;
        });
    };

    const getCandleTotalInventory = (invData) => {
        const candle = invData?.Candle || {};
        const batches = normalizeBatches(candle.batches);
        return batches.reduce((sum, b) => sum + (Number(b?.qtyRemaining) || 0), 0);
    };

    const getReservedCandleInventory = (ordersList) => {
        // Reserved = Candle quantity for orders that are NOT fulfilled.
        return (ordersList || []).reduce((sum, o) => {
            if ((o?.item || '').trim() !== 'Candle') return sum;
            const status = typeof o?.orderStatus === 'string' ? o.orderStatus.trim() : '';
            if (status === 'Fulfilled') return sum;
            const q = typeof o?.quantity === 'number' ? o.quantity : Number(o.quantity || 0);
            return sum + (Number.isFinite(q) ? q : 0);
        }, 0);
    };

    const getNeededSupply = (totalInv, reservedInv) => {
        const diff = (Number(reservedInv) || 0) - (Number(totalInv) || 0);
        return diff > 0 ? diff : 0;
    };

    const rangeLabel = useMemo(() => {
        if (range === "today") return "Today";
        if (range === "week") return "This Week";
        if (range === "mtd") return "This Month";
        if (range === "ytd") return "Year to Date";
        return "This Month";
    }, [range]);

    const pageTitle = useMemo(() => {
        if (active === "talks") return "Talks";
        if (active === "chapters") return "Chapters";
        if (active === "orders") return "Orders";
        if (active === "donations") return "Donations";
        if (active === "users") return "Users";
        return "National Dashboard";
    }, [active]);

    const pageSubtitle = useMemo(() => {
        if (active === "talks") return "Browse, review, and publish talks across all chapters.";
        if (active === "chapters") return "Manage schools, leadership, and chapter setup.";
        if (active === "orders") return "View orders, fulfillment status, and sales performance.";
        if (active === "donations") return "Review donation transactions, donor history, and tax reports.";
        if (active === "users") return "Manage members and permissions.";
        return "SOLE HQ overview — sales, chapters, talks, and operations.";
    }, [active]);

    useEffect(() => {
        let mounted = true;

        async function loadHomeMetrics() {
            setMetricsError("");

            try {
                // ---- Orders: revenue (range) + unfulfilled (all-time) + mini sales summary ----
                // Unfulfilled should be ALL unfulfilled orders (not limited to the selected range).
                let unfulfilledOrders = 0;
                const ordersList = [];

                const FULFILLED_STATUSES = new Set(["Fulfilled", "Delivered", "Picked Up", "Shipped"]);

                let ordersSnap;
                try {
                    ordersSnap = await getDocs(collection(db, COL.orders));
                } catch {
                    ordersSnap = null;
                }

                if (ordersSnap) {
                    ordersSnap.forEach((docSnap) => {
                        const d = docSnap.data() || {};

                        // Keep ONLY the fields we need.
                        ordersList.push({
                            id: docSnap.id,
                            date: d.date ?? d.createdAt ?? d.timestamp ?? null,
                            statusHistory: Array.isArray(d.statusHistory) ? d.statusHistory : [],
                            total: d.total ?? d.amount ?? d.totalCents ?? d.amountCents ?? 0,
                            quantity: d.quantity ?? 0,
                            item: d.item ?? "",
                            orderType: d.orderType ?? "",
                            paymentMethod: d.paymentMethod ?? "",
                            orderStatus: d.orderStatus ?? "",
                            fulfilled: d.fulfilled,
                            shipped: d.shipped,
                        });

                        // Unfulfilled = anything not in the fulfilled set.
                        const status = typeof d.orderStatus === "string" ? d.orderStatus.trim() : "";
                        const fulfilled =
                            FULFILLED_STATUSES.has(status) || d.fulfilled === true || d.shipped === true;
                        if (!fulfilled) unfulfilledOrders += 1;
                    });
                }

                // ---- Inventory: load docs + compute Candle inventory summary ----
                let inventoryData = {};
                try {
                    const invSnap = await getDocs(collection(db, 'inventory'));
                    invSnap.forEach((d) => {
                        inventoryData[d.id] = d.data() || {};
                    });
                } catch {
                    inventoryData = {};
                }

                const candleTotalInv = getCandleTotalInventory(inventoryData);
                const candleReservedInv = getReservedCandleInventory(ordersList);
                const candleNeeded = getNeededSupply(candleTotalInv, candleReservedInv);
                const candleExcess = Math.max((Number(candleTotalInv) || 0) - (Number(candleReservedInv) || 0), 0);

                const inventorySummary = {
                    candle: {
                        status: inventoryData?.Candle?.status || '—',
                        allowPickup: !!inventoryData?.Candle?.allowPickup,
                        totalInv: candleTotalInv,
                        reservedInv: candleReservedInv,
                        excess: candleExcess,
                        needed: candleNeeded,
                    },
                    shirt: {
                        status: inventoryData?.['T-Shirt']?.status || inventoryData?.['T Shirt']?.status || inventoryData?.Shirt?.status || '—',
                    },
                };

                const salesSummary = computeSalesSummary(ordersList, rangeStart);
                const revenueCents = salesSummary?.totalSalesCents ?? 0;

                // ---- Chapters: active ----
                // ---- Chapters: active + list ----
                // ---- Chapters: active + list ----
                // NOTE: some environments historically used different collection names.
                // We try a few common ones so the dashboard doesn't silently show 0 chapters.
                let activeChapters = 0;
                let chaptersList = [];

                const CHAPTER_COLLECTION_CANDIDATES = [COL.chapters, "schools", "campuses", "chapters_v2"];

                const loadChaptersFromCollection = async (colName) => {
                    const allSnap = await getDocs(collection(db, colName));
                    const list = [];
                    let activeCount = 0;

                    allSnap.forEach((d) => {
                        const data = d.data() || {};
                        const slug = (data.slug || data.schoolSlug || d.id || "").toString();
                        const campus = data.campus || data.name || data.schoolName || slug;
                        const isActive = !!data.active;
                        if (isActive) activeCount += 1;

                        list.push({
                            id: d.id,
                            slug,
                            campus,
                            active: isActive,
                            leaderName: data.leaderName || data.leader || data.contactName || data.primaryContactName || "",
                        });
                    });

                    // Sort: active first, then campus name
                    list.sort((a, b) => {
                        if (a.active !== b.active) return a.active ? -1 : 1;
                        return (a.campus || "").localeCompare(b.campus || "");
                    });

                    return { list, activeCount };
                };

                try {
                    let chosen = null;

                    for (const colName of CHAPTER_COLLECTION_CANDIDATES) {
                        try {
                            const { list, activeCount } = await loadChaptersFromCollection(colName);
                            if (list.length > 0) {
                                chaptersList = list;
                                activeChapters = activeCount;
                                chosen = colName;
                                break;
                            }
                        } catch {
                            // try next candidate
                        }
                    }

                    // If all candidates returned empty, do one last attempt on the default
                    // so we can still fail loudly if permissions are wrong.
                    if (!chosen) {
                        const { list, activeCount } = await loadChaptersFromCollection(COL.chapters);
                        chaptersList = list;
                        activeChapters = activeCount;
                    }
                } catch {
                    chaptersList = [];
                    activeChapters = 0;
                }

                // ---- Talks/Events: upcoming 14 days (from schools/{school}.events) ----
                // In this app, chapter talks/events live under each school doc.
                const now = new Date();
                const in14 = new Date(now);
                in14.setDate(in14.getDate() + 14);

                const toLocalMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

                const parseEventDate = (raw) => {
                    if (!raw) return null;

                    // date-only strings like "2026-01-26" should be treated as local-midnight
                    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
                        const [yyyy, mm, dd] = raw.trim().split("-").map(Number);
                        const d = new Date(yyyy, mm - 1, dd);
                        return isNaN(d) ? null : d;
                    }

                    // Firestore Timestamp
                    if (raw && typeof raw.toDate === "function") {
                        const d = raw.toDate();
                        return d instanceof Date && !isNaN(d) ? d : null;
                    }

                    // Date
                    if (raw instanceof Date) return isNaN(raw) ? null : raw;

                    // Anything else we can try to Date()
                    const d = new Date(raw);
                    return isNaN(d) ? null : d;
                };

                let upcomingTalks14d = 0;
                try {
                    const schoolsSnap = await getDocs(collection(db, "schools"));
                    const startMid = toLocalMidnight(now);
                    const endMid = toLocalMidnight(in14);
                    endMid.setDate(endMid.getDate() + 1); // inclusive end

                    schoolsSnap.forEach((docSnap) => {
                        const data = docSnap.data() || {};
                        const eventsRaw = data.events;

                        // Support both arrays and object-maps
                        const events = Array.isArray(eventsRaw)
                            ? eventsRaw
                            : eventsRaw && typeof eventsRaw === "object"
                                ? Object.values(eventsRaw)
                                : [];

                        events.forEach((ev) => {
                            if (!ev) return;
                            const dt = parseEventDate(ev.date || ev.startTime || ev.datetime || ev.start || null);
                            if (!dt) return;

                            const mid = toLocalMidnight(dt);
                            if (mid >= startMid && mid < endMid) upcomingTalks14d += 1;
                        });
                    });
                } catch {
                    // If we can't read schools/events, fall back to 0 (keeps UI stable)
                    upcomingTalks14d = 0;
                }

                // ---- Subscribers: count ----
                let emailSubscribers = 0;
                try {
                    const subsSnap = await getDocs(collection(db, COL.subscribers));
                    emailSubscribers = subsSnap.size;
                } catch {
                    emailSubscribers = 0;
                }

                if (!mounted) return;

                setMetrics({
                    revenueCents,
                    unfulfilledOrders,
                    activeChapters,
                    upcomingTalks14d,
                    emailSubscribers,
                    salesSummary,
                    inventoryData,
                    inventorySummary,
                    chaptersList,
                });
            } catch {
                if (!mounted) return;
                setMetricsError("Could not load dashboard metrics from Firebase yet (schema may not match).");
                setMetrics({
                    revenueCents: null,
                    unfulfilledOrders: null,
                    activeChapters: null,
                    upcomingTalks14d: null,
                    emailSubscribers: null,
                    salesSummary: null,
                    inventoryData: null,
                    inventorySummary: null,
                    chaptersList: null,
                });
            }
        }

        if (active === "home") {
            loadHomeMetrics();
            loadHqNotesAndAlerts();
        }

        return () => {
            mounted = false;
        };
    }, [active, rangeStart]);

    return (
        <div className="nd-page">
            <Navbar />

            <div className="nd-shell">
                {/* Sidebar */}
                <aside className="nd-sidenav" aria-label="National admin navigation">
                    <div className="nd-sidenav-head">
                        <div className="nd-sidenav-title">National</div>
                        <div className="nd-sidenav-subtitle">SOLE HQ</div>
                    </div>

                    <div className="nd-sidenav-section">
                        <SideNavItem active={active === "home"} onClick={() => setActive("home")} icon="" label="Home" />
                        <SideNavItem active={active === "talks"} onClick={() => setActive("talks")} icon="" label="Talks" />
                        <SideNavItem active={active === "chapters"} onClick={() => setActive("chapters")} icon="" label="Chapters" />
                        <SideNavItem active={active === "orders"} onClick={() => setActive("orders")} icon="" label="Orders" />
                        <SideNavItem active={active === "donations"} onClick={() => setActive("donations")} icon="" label="Donations" />
                        <SideNavItem active={active === "users"} onClick={() => setActive("users")} icon="" label="Users" />
                    </div>

                    <div className="nd-sidenav-divider" />

                    <div className="nd-sidenav-section nd-sidenav-section--muted">
                        <div className="nd-sidenav-mini">Quick Links</div>
                        <Link target="_blank" className="nd-sidenav-link" to="https://docs.google.com/spreadsheets/d/1-MiYE6bK06JNjKirI-AaqZbE3IvXok2ifhVZxbbayYk/edit?gid=0#gid=0">
                            Curriculum
                        </Link>
                        <Link target="_blank" className="nd-sidenav-link" to="https://docs.google.com/spreadsheets/d/1KK60naAM1po-Qyv5mGgeDzBdCThnPVlIXou6nadt_Kc/edit?gid=614353357#gid=614353357">
                            Media Planner
                        </Link>
                        <Link target="_blank" className="nd-sidenav-link" to="/email">
                            Email Builder
                        </Link>
                    </div>
                </aside>

                {/* Content */}
                <main className="nd-content">
                    {/* Header */}
                    <div className="nd-header">
                        <div className="nd-header-left">
                            <h1 className="nd-title">{pageTitle}</h1>
                            <div className="nd-subtitle">{pageSubtitle}</div>
                        </div>

                        <div className="nd-header-right">
                            {active === "home" ? (
                                <Segmented
                                    value={range}
                                    onChange={setRange}
                                    options={[
                                        { value: "today", label: "Today" },
                                        { value: "week", label: "This Week" },
                                        { value: "mtd", label: "This Month" },
                                        { value: "ytd", label: "YTD" },
                                    ]}
                                />
                            ) : null}

                            <div className="nd-header-cta">
                                {active === "home" ? (
                                    <>
                                        {/* <button className="nd-btn nd-btn-primary" type="button">
                                            Add School
                                        </button> */}
                                        {/* <button className="nd-btn" type="button">
                                            Add Product
                                        </button> */}
                                    </>
                                ) : null}

                                {active === "talks" ? (
                                    <>
                                        <Link className="nd-btn nd-btn-primary" to="/editor">
                                            + New Talk
                                        </Link>
                                    </>
                                ) : null}

                                {active === "orders" ? (
                                    <>
                                        <Link className="nd-btn nd-btn-primary" to="/orders">
                                            Open Orders Page
                                        </Link>
                                    </>
                                ) : null}
                                {active === "chapters" ? (
                                    <>
                                        <button className="nd-btn nd-btn-primary" type="button" onClick={() => window.location.assign("/edit-chapter/new")}
                                        >
                                            + New Chapter
                                        </button>
                                    </>
                                ) : null}
                                {active === "users" ? (
                                    <>
                                        <Link className="nd-btn" to="/users">Open Users Page</Link>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* Views */}
                    {active === "home" ? (
                        <>
                            {/* Overview metrics */}
                            <div className="nd-stats-grid" aria-label="Overview metrics">
                                <StatCard label={`Total Revenue (${rangeLabel})`} value={formatMoney(metrics.revenueCents)} sublabel="All products" />
                                <StatCard label="Unfulfilled Orders" value={metrics.unfulfilledOrders ?? "—"} sublabel="Needs action" />
                                <StatCard label="Active Chapters" value={metrics.activeChapters ?? "—"} sublabel="Live this semester" />
                                <StatCard label="Upcoming Talks (14d)" value={metrics.upcomingTalks14d ?? "—"} sublabel="Across all schools" />
                                <StatCard label="Email Subscribers" value={metrics.emailSubscribers ?? "—"} sublabel="National list" />
                            </div>
                            {metricsError ? (
                                <div style={{ marginTop: "10px", marginBottom: "8px", fontWeight: 700, opacity: 0.85 }} role="status">
                                    {metricsError}
                                </div>
                            ) : null}

                            {/* Main grid: overview, widgets, and info panels */}
                            <div className="nd-main-grid">
                                {/* Left column */}
                                <div className="nd-col">
                                    <Panel
                                        title="Sales & Store"
                                        actions={
                                            <>
                                                <button className="nd-btn" type="button" onClick={() => setActive("orders")}>View Orders</button>
                                                <button
                                                    className="nd-btn"
                                                    type="button"
                                                    onClick={() => {
                                                        setActive("orders");
                                                        setOrdersInventoryToken((t) => t + 1);
                                                    }}
                                                >
                                                    Edit Inventory
                                                </button>
                                            </>
                                        }
                                    >
                                        <div className="nd-two-up">
                                            <div className="nd-placeholder">
                                                <div className="nd-placeholder-title">Sales Summary</div>
                                                {metrics.salesSummary ? (
                                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px", marginTop: "10px" }}>
                                                        <div>
                                                            <div style={{ fontSize: "0.72rem", opacity: 0.7, fontWeight: 700, letterSpacing: "0.04em" }}>TOTAL SALES ({rangeLabel.toUpperCase()})</div>
                                                            <div style={{ fontSize: "1.35rem", fontWeight: 800 }}>{formatMoney(metrics.salesSummary.totalSalesCents)}</div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: "0.72rem", opacity: 0.7, fontWeight: 700, letterSpacing: "0.04em" }}>TOTAL ORDERS</div>
                                                            <div style={{ fontSize: "1.35rem", fontWeight: 800 }}>{metrics.salesSummary.totalOrders}</div>
                                                        </div>

                                                        <div>
                                                            <div style={{ fontSize: "0.72rem", opacity: 0.7, fontWeight: 700, letterSpacing: "0.04em" }}>AVG ORDER</div>
                                                            <div style={{ fontSize: "1.15rem", fontWeight: 800 }}>{formatMoney(metrics.salesSummary.avgOrderValueCents)}</div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: "0.72rem", opacity: 0.7, fontWeight: 700, letterSpacing: "0.04em" }}>UNITS SOLD</div>
                                                            <div style={{ fontSize: "1.15rem", fontWeight: 800 }}>{(metrics.salesSummary.totalCandles || 0) + (metrics.salesSummary.totalShirts || 0)}</div>
                                                        </div>

                                                        <div style={{ gridColumn: "1 / -1", marginTop: "2px" }}>
                                                            <div style={{ fontSize: "0.78rem", fontWeight: 800, marginBottom: "6px" }}>Sales by product</div>
                                                            {(() => {
                                                                const candle = metrics.salesSummary.candleSalesCents || 0;
                                                                const shirt = metrics.salesSummary.shirtSalesCents || 0;
                                                                const max = Math.max(candle, shirt, 1);
                                                                const pct = (v) => `${Math.round((v / max) * 100)}%`;
                                                                return (
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                                                        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 90px", gap: "10px", alignItems: "center" }}>
                                                                            <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>Candles</div>
                                                                            <div style={{ height: "10px", borderRadius: "999px", background: "rgba(15,23,42,0.08)", overflow: "hidden" }}>
                                                                                <div style={{ height: "100%", width: pct(candle), background: "#f97316" }} />
                                                                            </div>
                                                                            <div style={{ fontSize: "0.85rem", fontWeight: 800, textAlign: "right" }}>{formatMoney(candle)}</div>
                                                                        </div>
                                                                        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 90px", gap: "10px", alignItems: "center" }}>
                                                                            <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>T-Shirts</div>
                                                                            <div style={{ height: "10px", borderRadius: "999px", background: "rgba(15,23,42,0.08)", overflow: "hidden" }}>
                                                                                <div style={{ height: "100%", width: pct(shirt), background: "#3b82f6" }} />
                                                                            </div>
                                                                            <div style={{ fontSize: "0.85rem", fontWeight: 800, textAlign: "right" }}>{formatMoney(shirt)}</div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {Object.keys(metrics.salesSummary.byPayment || {}).length ? (
                                                            <div style={{ gridColumn: "1 / -1", marginTop: "6px" }}>
                                                                <div style={{ fontSize: "0.78rem", fontWeight: 800, marginBottom: "6px" }}>By payment method</div>
                                                                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "6px 10px" }}>
                                                                    {Object.entries(metrics.salesSummary.byPayment)
                                                                        .sort((a, b) => (b?.[1]?.salesCents || 0) - (a?.[1]?.salesCents || 0))
                                                                        .slice(0, 4)
                                                                        .map(([method, s]) => (
                                                                            <React.Fragment key={method}>
                                                                                <div style={{ fontSize: "0.85rem", fontWeight: 700, opacity: 0.9 }}>{method}</div>
                                                                                <div style={{ fontSize: "0.85rem", fontWeight: 800 }}>{formatMoney(s?.salesCents || 0)}</div>
                                                                            </React.Fragment>
                                                                        ))}
                                                                </div>
                                                            </div>
                                                        ) : null}

                                                    </div>
                                                ) : (
                                                    <div className="nd-placeholder-text">Loading sales summary…</div>
                                                )}
                                            </div>

                                            <div className="nd-placeholder">
                                                <div className="nd-placeholder-title">Inventory</div>
                                                {metrics.inventorySummary ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 10px', alignItems: 'center' }}>
                                                            <div style={{ fontSize: '0.85rem', fontWeight: 800 }}>Candles</div>
                                                            <div style={{ fontSize: '0.85rem', fontWeight: 800, textAlign: 'right' }}>{metrics.inventorySummary.candle.status}</div>

                                                            <div style={{ fontSize: '0.82rem', opacity: 0.85 }}>Allow pickup</div>
                                                            <div style={{ fontSize: '0.82rem', fontWeight: 700, textAlign: 'right' }}>{metrics.inventorySummary.candle.allowPickup ? 'Yes' : 'No'}</div>
                                                        </div>

                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                                                gap: '10px',
                                                                paddingTop: '10px',
                                                                borderTop: '1px solid rgba(15,23,42,0.10)',
                                                            }}
                                                        >
                                                            <div>
                                                                <div style={{ fontSize: '0.72rem', opacity: 0.7, fontWeight: 700, letterSpacing: '0.04em' }}>TOTAL INVENTORY</div>
                                                                <div style={{ fontSize: '1.15rem', fontWeight: 800 }}>{metrics.inventorySummary.candle.totalInv}</div>
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: '0.72rem', opacity: 0.7, fontWeight: 700, letterSpacing: '0.04em' }}>RESERVED</div>
                                                                <div style={{ fontSize: '1.15rem', fontWeight: 800 }}>{metrics.inventorySummary.candle.reservedInv}</div>
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: '0.72rem', opacity: 0.7, fontWeight: 700, letterSpacing: '0.04em' }}>EXCESS SUPPLY</div>
                                                                <div style={{ fontSize: '1.15rem', fontWeight: 800 }}>{metrics.inventorySummary.candle.excess}</div>
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: '0.72rem', opacity: 0.7, fontWeight: 700, letterSpacing: '0.04em' }}>NEEDED SUPPLY</div>
                                                                <div style={{ fontSize: '1.15rem', fontWeight: 800 }}>{metrics.inventorySummary.candle.needed}</div>
                                                            </div>
                                                        </div>

                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 10px', marginTop: '6px' }}>
                                                            <div style={{ fontSize: '0.85rem', fontWeight: 800 }}>T-Shirts</div>
                                                            <div style={{ fontSize: '0.85rem', fontWeight: 800, textAlign: 'right' }}>{metrics.inventorySummary.shirt.status}</div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="nd-placeholder-text">Loading inventory…</div>
                                                )}
                                            </div>
                                        </div>
                                    </Panel>

                                    <Panel
                                        title="Chapters"
                                        actions={
                                            <>
                                                <button className="nd-btn" type="button" onClick={() => setActive("chapters")}>
                                                    Manage Chapters
                                                </button>
                                            </>
                                        }
                                    >
                                        <div className="nd-placeholder">
                                            <div className="nd-placeholder-title">All Chapters</div>

                                            {metrics.chaptersList === null ? (
                                                <div className="nd-placeholder-text">Loading chapters…</div>
                                            ) : metrics.chaptersList.length === 0 ? (
                                                <div className="nd-placeholder-text">No chapters found yet.</div>
                                            ) : (
                                                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
                                                    {metrics.chaptersList.map((c) => (
                                                        <div
                                                            key={c.id}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                gap: "12px",
                                                                padding: "10px 12px",
                                                                borderRadius: "12px",
                                                                border: "1px solid rgba(15,23,42,0.10)",
                                                                background: "rgba(255,255,255,0.65)",
                                                            }}
                                                        >
                                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                                <div style={{ fontWeight: 800, fontSize: "0.95rem" }}>{c.campus}</div>
                                                                <div style={{ fontSize: "0.82rem", opacity: 0.75 }}>
                                                                    {c.slug ? `/${c.slug}` : ""}{c.leaderName ? ` • ${c.leaderName}` : ""}
                                                                </div>
                                                            </div>

                                                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                                <span
                                                                    style={{
                                                                        fontSize: "0.78rem",
                                                                        fontWeight: 800,
                                                                        padding: "6px 10px",
                                                                        borderRadius: "999px",
                                                                        background: c.active ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.18)",
                                                                        color: c.active ? "#166534" : "#334155",
                                                                        border: "1px solid rgba(15,23,42,0.08)",
                                                                    }}
                                                                >
                                                                    {c.active ? "Active" : "Inactive"}
                                                                </span>

                                                                <Link className="nd-btn" to={`/manage/${encodeURIComponent(c.slug || c.id)}`}>
                                                                    Open
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Panel>

                                    <Panel
                                        title="Operations & Alerts"
                                        actions={
                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <button className="nd-btn nd-btn-primary" type="button" onClick={() => setAlertModalOpen(true)}>
                                                    + Add Alert
                                                </button>
                                                <button className="nd-btn" type="button" onClick={loadHqNotesAndAlerts}>
                                                    Refresh
                                                </button>
                                            </div>
                                        }
                                    >
                                        {notesError ? (
                                            <div style={{ marginBottom: "8px", fontWeight: 700, opacity: 0.8 }}>{notesError}</div>
                                        ) : null}

                                        <div className="nd-alert-list">
                                            {hqAlerts.length === 0 ? (
                                                <div className="nd-alert-item">
                                                    <div className="nd-alert-title">No alerts yet</div>
                                                    <div className="nd-alert-subtitle">Add an alert for important ops items (inventory, shipments, speaker follow-ups).</div>
                                                </div>
                                            ) : (
                                                hqAlerts.map((a) => (
                                                    <div key={a.id} className="nd-alert-item" style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div className="nd-alert-title" style={{ whiteSpace: "pre-wrap" }}>{a.text}</div>
                                                            <div className="nd-alert-subtitle" style={{ marginTop: "4px" }}>
                                                                {a.authorName ? `${a.authorName}` : ""}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="nd-btn"
                                                            title="Dismiss"
                                                            aria-label="Dismiss"
                                                            onClick={() => dismissHqItem("alert", a.id)}
                                                            style={{
                                                                borderRadius: "999px",
                                                                padding: "8px 10px",
                                                                fontWeight: 900,
                                                                flex: "0 0 auto",
                                                            }}
                                                        >
                                                            ✓
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </Panel>
                                </div>

                                {/* Right column: widgets and info panels */}
                                <div className="nd-col">
                                    <Panel
                                        title="National Team"
                                        actions={
                                            <Link className="nd-btn" to="/users">
                                                Manage Members
                                            </Link>
                                        }
                                    >
                                        <div className="nd-placeholder">
                                            {/* <div className="nd-placeholder-title">Members & Roles</div> */}
                                            <NationalTeamWidget />
                                        </div>
                                    </Panel>

                                    <Panel
                                        title="Notes"
                                        actions={
                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <button className="nd-btn nd-btn-primary" type="button" onClick={() => setNoteModalOpen(true)}>
                                                    + Add Note
                                                </button>
                                                <button className="nd-btn" type="button" onClick={loadHqNotesAndAlerts}>
                                                    Refresh
                                                </button>
                                            </div>
                                        }
                                    >
                                        <div className="nd-placeholder">
                                            <div className="nd-placeholder-title">HQ Notes</div>

                                            {hqNotes.length === 0 ? (
                                                <div className="nd-placeholder-text">No notes yet. Add a note for reminders, follow-ups, or operational context.</div>
                                            ) : (
                                                <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
                                                    {hqNotes.map((n) => (
                                                        <div
                                                            key={n.id}
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                gap: "12px",
                                                                padding: "12px",
                                                                borderRadius: "14px",
                                                                border: "1px solid rgba(15,23,42,0.10)",
                                                                background: "rgba(255,255,255,0.65)",
                                                            }}
                                                        >
                                                            <div style={{ minWidth: 0 }}>
                                                                <div style={{ fontWeight: 800, whiteSpace: "pre-wrap" }}>{n.text}</div>
                                                                <div style={{ fontSize: "0.82rem", opacity: 0.75, marginTop: "4px" }}>
                                                                    {n.authorName ? `${n.authorName}` : ""}
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="nd-btn"
                                                                title="Dismiss"
                                                                aria-label="Dismiss"
                                                                onClick={() => dismissHqItem("note", n.id)}
                                                                style={{
                                                                    borderRadius: "999px",
                                                                    padding: "8px 10px",
                                                                    fontWeight: 900,
                                                                    flex: "0 0 auto",
                                                                }}
                                                            >
                                                                ✓
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Panel>
                                </div>
                            </div>
                            {/* MODALS for Notes and Alerts */}
                            <SimpleModal open={noteModalOpen} title="Add HQ Note" onClose={() => setNoteModalOpen(false)}>
                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    <div style={{ fontWeight: 800, fontSize: "0.9rem" }}>Your name</div>
                                    <div
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: "12px",
                                            border: "1px solid rgba(15,23,42,0.18)",
                                            background: "rgba(15,23,42,0.04)",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {currentUserLabel || "Unknown"}
                                    </div>

                                    <label style={{ fontWeight: 800, fontSize: "0.9rem", marginTop: "6px" }}>Note</label>
                                    <textarea
                                        value={newNoteText}
                                        onChange={(e) => setNewNoteText(e.target.value)}
                                        placeholder="Write a note…"
                                        rows={5}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: "12px",
                                            border: "1px solid rgba(15,23,42,0.18)",
                                            outline: "none",
                                            resize: "vertical",
                                        }}
                                    />

                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                                        <button className="nd-btn" type="button" onClick={() => setNoteModalOpen(false)}>
                                            Cancel
                                        </button>
                                        <button className="nd-btn nd-btn-primary" type="button" onClick={() => addHqItem("note")}>
                                            Save Note
                                        </button>
                                    </div>
                                </div>
                            </SimpleModal>

                            <SimpleModal open={alertModalOpen} title="Add HQ Alert" onClose={() => setAlertModalOpen(false)}>
                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    <div style={{ fontWeight: 800, fontSize: "0.9rem" }}>Your name</div>
                                    <div
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: "12px",
                                            border: "1px solid rgba(15,23,42,0.18)",
                                            background: "rgba(15,23,42,0.04)",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {currentUserLabel || "Unknown"}
                                    </div>

                                    <label style={{ fontWeight: 800, fontSize: "0.9rem", marginTop: "6px" }}>Alert</label>
                                    <textarea
                                        value={newAlertText}
                                        onChange={(e) => setNewAlertText(e.target.value)}
                                        placeholder="Write an alert…"
                                        rows={4}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: "12px",
                                            border: "1px solid rgba(15,23,42,0.18)",
                                            outline: "none",
                                            resize: "vertical",
                                        }}
                                    />

                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                                        <button className="nd-btn" type="button" onClick={() => setAlertModalOpen(false)}>
                                            Cancel
                                        </button>
                                        <button className="nd-btn nd-btn-primary" type="button" onClick={() => addHqItem("alert")}>
                                            Save Alert
                                        </button>
                                    </div>
                                </div>
                            </SimpleModal>
                        </>
                    ) : null}

                    {active === "talks" ? (
                        <div className="nd-fullscreen">
                            <TalksDashboardPanel embedded={false} controls="newTalkOnly" />
                        </div>
                    ) : null}

                    {active === "chapters" ? (
                        <div className="nd-fullscreen">
                            <ChaptersPage embedded />
                        </div>
                    ) : null}

                    {active === "orders" ? (
                        <div className="nd-fullscreen">
                            <Orders
                                embedded
                                inventoryOpenToken={ordersInventoryToken}
                            />
                        </div>
                    ) : null}
                    {active === "donations" ? (
                        <div className="nd-fullscreen">
                            <DonationReport />
                        </div>
                    ) : null}
                    {active === "users" ? (
                        <div className="nd-fullscreen">
                            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                                {/* <div className="nd-card nd-panel" style={{ padding: "16px" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                                        <div>
                                            <div style={{ fontWeight: 900, fontSize: "1.05rem" }}>
                                                Users
                                            </div>
                                        </div>
                                    </div>
                                </div> */}

                                <UsersManager embedded title="Users" />
                            </div>
                        </div>
                    ) : null}
                </main>
            </div>
        </div>
    );
}